import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSupabaseClient } from '../lib/supabase';
import type { Course, CourseWithSessions, CourseSession } from '../types';

export function useCourses() {
  const supabase = getSupabaseClient();

  return useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Course[];
    },
  });
}

export function useCourse(courseId: string) {
  const supabase = getSupabaseClient();

  return useQuery({
    queryKey: ['course', courseId],
    queryFn: async () => {
      // Get course with its sessions
      const { data: course, error: courseError } = await supabase
        .from('courses')
        .select(`
          *,
          sessions:course_sessions(
            id,
            title,
            description,
            course_id,
            next_session_id,
            order,
            is_free,
            created_at,
            updated_at
          )
        `)
        .eq('id', courseId)
        .single();

      if (courseError) throw courseError;

      // Sort sessions by order
      if (course.sessions) {
        course.sessions.sort((a: CourseSession, b: CourseSession) => a.order - b.order);
      }

      return course as CourseWithSessions;
    },
    enabled: !!courseId,
  });
}

export function useCourseSession(sessionId: string) {
  const supabase = getSupabaseClient();

  return useQuery({
    queryKey: ['course-session', sessionId],
    queryFn: async () => {
      // Get session with its materials
      const { data, error } = await supabase
        .from('course_sessions')
        .select(`
          *,
          materials:session_materials(
            id,
            order,
            material:materials(*)
          )
        `)
        .eq('id', sessionId)
        .single();

      if (error) throw error;

      // Sort materials by order and flatten structure
      if (data.materials) {
        data.materials.sort((a: any, b: any) => a.order - b.order);
      }

      return data;
    },
    enabled: !!sessionId,
  });
}
